import { supabase } from "../../providers/supabase/client";
import { PlaceHotelTable, PlaceAerolineTable } from "../../util/interfaces";

interface DestinationPdf {
  title: string;
  url: string;
}

interface AirportItem {
  code: string;
  name?: string;
}

interface Destination {
  name: string;
  country: string;
  code?: string | null;
  airports?: AirportItem[] | null;
  image_url?: string | null;
  cover_image_url?: string | null;
  slogan?: string | null;
  info_pdf_url?: string | null;
  info_pdfs?: DestinationPdf[] | null;
  services?: string[] | null;
  notes?: string | null;
  status?: 'active' | 'maintenance';
  seasons?: any[] | null;
  travel_requirements?: any[] | null;
}

// Shared select string — explicit columns to reduce payload
const DESTINATION_SELECT = `
  id, created_at, name, country, code, airports, image_url, cover_image_url, slogan, info_pdf_url, info_pdfs, services, notes, status, seasons, travel_requirements,
  places_hotels(
      id, places_id, hotels_id,
      hotels(
          id, name, stars, description, main_image_url, gallery_urls, video_urls,
          hotel_configurations(
              id, hotel_id, room_type_id, is_non_refundable, meal_plan, notes, is_active, room_image_urls, additional_info, custom_name,
              room_types(id, name)
          )
      )
  ),
  tours(id, name, kidPrice, adultPrice, duration, description, operating_days, service_type, itinerary, includes, not_includes, notes, image_url, gallery_urls),
  places_aerolines(
      id, places_id, aerolines_id,
      aerolines(
          id, name, logo_url,
          airline_configurations(
              id, airline_id, flight_type_id, notes, is_active,
              flight_types(id, name)
          )
      )
  ),
  places_operators(
      id, places_id, providers_id,
      providers(id, name, ruc, razon_social, description, provider_type, logo_url)
  )
`;

const DESTINATION_SELECT_FALLBACK = `
  id, created_at, name, country, code, airports, image_url, info_pdf_url, info_pdfs, services, notes, status, seasons, travel_requirements,
  places_hotels(
      id, places_id, hotels_id,
      hotels(
          id, name, stars, description, main_image_url, gallery_urls, video_urls,
          hotel_configurations(
              id, hotel_id, room_type_id, is_non_refundable, meal_plan, notes, is_active,
              room_types(id, name)
          )
      )
  ),
  tours(id, name, kidPrice, adultPrice, duration, image_url, gallery_urls),
  places_aerolines(
      id, places_id, aerolines_id,
      aerolines(
          id, name, logo_url,
          airline_configurations(
              id, airline_id, flight_type_id, notes, is_active,
              flight_types(id, name)
          )
      )
  )
`;

export const fetchDestinations = async () => {
  const res = await supabase.from("places").select(DESTINATION_SELECT);
  if (res.error && (res.error.message.includes("cover_image_url") || res.error.message.includes("slogan") || res.error.message.includes("column"))) {
    console.warn("Falling back to DESTINATION_SELECT_FALLBACK because cover_image_url or slogan column was not found.");
    return await supabase.from("places").select(DESTINATION_SELECT_FALLBACK);
  }
  return res;
};

export const createDestination = async (destination: Destination) => {
  const res = await supabase.from("places").insert([destination]).select(DESTINATION_SELECT);
  if (res.error && (res.error.message.includes("cover_image_url") || res.error.message.includes("slogan") || res.error.message.includes("column"))) {
    const { cover_image_url, slogan, ...rest } = destination;
    return await supabase.from("places").insert([rest]).select(DESTINATION_SELECT_FALLBACK);
  }
  return res;
};

export const addManyDestinations = async (destinations: Destination[]) => {
  const res = await supabase.from("places").insert(destinations).select(DESTINATION_SELECT);
  if (res.error && (res.error.message.includes("cover_image_url") || res.error.message.includes("slogan") || res.error.message.includes("column"))) {
    const cleaned = destinations.map(({ cover_image_url, slogan, ...rest }) => rest);
    return await supabase.from("places").insert(cleaned).select(DESTINATION_SELECT_FALLBACK);
  }
  return res;
};

export const updateDestination = async (
  id: number,
  destination: Destination
) => {
  const res = await supabase.from("places").update(destination).eq("id", id).select(DESTINATION_SELECT);
  if (res.error && (res.error.message.includes("cover_image_url") || res.error.message.includes("slogan") || res.error.message.includes("column"))) {
    const { cover_image_url, slogan, ...rest } = destination;
    return await supabase.from("places").update(rest).eq("id", id).select(DESTINATION_SELECT_FALLBACK);
  }
  return res;
};

export const deleteDestination = async (id: number) => {
  return await supabase.from("places").delete().eq("id", id);
};

export const assignHotelsDestination = async (
  hotelPlaces: PlaceHotelTable[]
) => {
  return await supabase.from("places_hotels").insert(hotelPlaces).select(`
      id, places_id, hotels_id,
      hotels(id, name, stars, description, main_image_url)
    `);
};
export const removeHotelDestination = async (id: number) => {
  return await supabase.from("places_hotels").delete().eq("id", id);
};
export const assignAerolinesDestination = async (
  aerolinePlace: PlaceAerolineTable[]
) => {
  return await supabase.from("places_aerolines").insert(aerolinePlace).select(`
      id, places_id, aerolines_id,
      aerolines(id, name, logo_url)
    `);
};
export const removeAerolineDestination = async (id: number) => {
  return await supabase.from("places_aerolines").delete().eq("id", id);
};
export const assignOperatorsDestination = async (
  operatorPlace: { places_id: number; providers_id: number }[]
) => {
  return await supabase.from("places_operators").insert(operatorPlace).select(`
      id, places_id, providers_id,
      providers(id, name, ruc, razon_social, description, provider_type, logo_url)
    `);
};
export const removeOperatorDestination = async (id: number) => {
  return await supabase.from("places_operators").delete().eq("id", id);
};
